import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, trainingTone, validityBadge } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Eye, Download, Flame, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = { planlandı: "pending", gerçekleşti: "active" };

function PersonnelDetailDialog({ personnelId, onClose }) {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get(`/personnel/${personnelId}`).then(({ data }) => setP(data)).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [personnelId]); // eslint-disable-line

  const groups = {};
  (p?.documents || []).forEach((d) => { const k = d.cert_label || "Diğer Eğitim Belgesi"; (groups[k] = groups[k] || []).push(d); });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display">{p ? `${p.name} ${p.surname}` : "Personel"}</DialogTitle></DialogHeader>
        {!p ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">{p.duty}</span>
              <StatusBadge tone={trainingTone(p.training_status)}>{p.training_status}</StatusBadge>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Eğitim Geçmişi</h4>
              {(p.trainings || []).length === 0 ? <p className="text-sm text-slate-400">Eğitim kaydı yok.</p> : (
                <div className="divide-y divide-slate-100">
                  {p.trainings.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-1.5 text-sm" data-testid={`ctraining-tr-${t.id}`}>
                      <span>{t.training_date ? formatDate(t.training_date) : "Tarih girilmedi"} → {t.valid_until ? formatDate(t.valid_until) : "—"}</span>
                      <StatusBadge tone={t.status === "Geçerli" ? "active" : "overdue"}>{t.status}</StatusBadge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {p.src5 && (() => { const b = validityBadge(p.src5.valid_until); return (
              <div data-testid="ctraining-src5">
                <h4 className="text-sm font-semibold text-slate-700 mb-1">SRC5 Belgesi</h4>
                <div className="rounded border border-slate-200 p-2.5 text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span>SRC5 No: {p.src5.number || "—"}</span>
                    <StatusBadge tone={b.tone}>{b.text}</StatusBadge>
                  </div>
                  <div className="text-xs text-slate-500">
                    Belge tarihi: {p.src5.doc_date ? formatDate(p.src5.doc_date) : "—"} · Geçerlilik: {p.src5.valid_until ? formatDate(p.src5.valid_until) : "Tarih girilmedi"}
                  </div>
                  {p.src5.pdf && (
                    <div className="flex items-center gap-1 pt-1">
                      <Button variant="ghost" size="sm" onClick={() => previewFile(`/documents/${p.src5.pdf.id}/download`)} data-testid="ctraining-src5-preview"><Eye className="h-4 w-4 mr-1" /> Önizle</Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadFile(`/documents/${p.src5.pdf.id}/download`, `${p.src5.pdf.name}.pdf`)} data-testid="ctraining-src5-download"><Download className="h-4 w-4 mr-1" /> İndir</Button>
                    </div>
                  )}
                </div>
              </div>
            ); })()}

            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Yayımlanmış Belgeler</h4>
              {Object.keys(groups).length === 0 ? <p className="text-sm text-slate-400">Yayımlanmış belge yok.</p> : (
                <div className="space-y-3">
                  {Object.entries(groups).map(([label, docs]) => (
                    <div key={label}>
                      <div className="text-xs font-semibold text-slate-500 mb-1">{label}</div>
                      <div className="divide-y divide-slate-100">
                        {docs.map((d) => (
                          <div key={d.id} className="flex items-center justify-between gap-2 py-1.5 text-sm" data-testid={`ctraining-doc-${d.id}`}>
                            <span className="truncate min-w-0 flex-1" title={`${d.name}.${d.ext}`}>{d.name}.{d.ext}</span>
                            <div className="flex items-center gap-1 shrink-0">
                              {d.ext === "pdf" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => previewFile(`/documents/${d.id}/download`)} data-testid={`ctraining-doc-preview-${d.id}`}><Eye className="h-4 w-4" /></Button>}
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(`/documents/${d.id}/download`, `${d.name}.${d.ext}`)}><Download className="h-4 w-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerTraining() {
  const { user } = useAuth();
  const firmId = user.firm_id;
  const [params, setParams] = useSearchParams();
  const [drills, setDrills] = useState(null);
  const [personnel, setPersonnel] = useState(null);
  const [selPersonnel, setSelPersonnel] = useState(params.get("personnel") || null);

  useEffect(() => {
    if (!firmId) { setDrills([]); setPersonnel([]); return; }
    api.get(`/firms/${firmId}/drills`).then(({ data }) => setDrills(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get(`/firms/${firmId}/personnel`).then(({ data }) => setPersonnel(data)).catch(() => setPersonnel([]));
  }, [firmId]);

  const closeDetail = () => { setSelPersonnel(null); if (params.get("personnel")) { params.delete("personnel"); setParams(params, { replace: true }); } };

  if (!firmId) return <p className="text-sm text-slate-500">Firma bağlantınız bulunamadı.</p>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Eğitim ve Tatbikatlar</h1>
        <p className="text-sm text-slate-500 mt-1">Personel eğitim durumları, yayımlanmış belgeler ve tatbikatlar.</p>
      </div>

      {/* Personel Eğitimleri */}
      <div className="space-y-3">
        <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><GraduationCap className="h-5 w-5 text-sky-600" /> Personel Eğitimleri</h3>
        {personnel === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> : (
          personnel.length === 0 ? <p className="text-sm text-slate-400">Aktif personel yok.</p> : (
            <div className="grid sm:grid-cols-2 gap-2">
              {personnel.map((p) => (
                <Card key={p.id} className="border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => setSelPersonnel(p.id)} data-testid={`ctraining-personnel-${p.id}`}>
                  <CardContent className="py-3 flex items-center justify-between gap-2">
                    <div><div className="font-medium text-sm text-slate-800">{p.name} {p.surname}</div><div className="text-xs text-slate-500">{p.duty}</div></div>
                    <StatusBadge tone={trainingTone(p.training_status)}>{p.training_status}</StatusBadge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}
      </div>

      {/* Tatbikatlar */}
      <div className="space-y-3">
        <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><Flame className="h-5 w-5 text-orange-500" /> Tatbikatlar</h3>
        {drills === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> : (
          drills.length === 0 ? <p className="text-sm text-slate-400" data-testid="ctraining-empty">Henüz tatbikat kaydı yok.</p> : (
            <div className="space-y-2">
              {drills.map((d) => (
                <Card key={d.id} className="border-slate-200" data-testid={`ctraining-drill-${d.id}`}>
                  <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Flame className={`h-5 w-5 shrink-0 ${d.pdf ? "text-orange-500" : "text-slate-300"}`} />
                      <div>
                        <div className="font-medium text-sm text-slate-800">{d.name} {d.year && `(${d.year})`}</div>
                        <div className="text-xs text-slate-500">
                          {d.planned_date ? `Planlanan: ${formatDate(d.planned_date)}` : "Planlanan tarih girilmedi"}
                          {d.actual_date ? ` · Gerçekleşen: ${formatDate(d.actual_date)}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge tone={STATUS_TONE[d.status] || "muted"}>{d.status}</StatusBadge>
                      {d.pdf && <>
                        <Button variant="ghost" size="sm" onClick={() => previewFile(`/documents/${d.pdf.id}/download`)} data-testid={`ctraining-preview-${d.id}`}><Eye className="h-4 w-4 mr-1" /> Önizle</Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadFile(`/documents/${d.pdf.id}/download`, `${d.pdf.name}.pdf`)} data-testid={`ctraining-download-${d.id}`}><Download className="h-4 w-4 mr-1" /> İndir</Button>
                      </>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}
      </div>

      {selPersonnel && <PersonnelDetailDialog personnelId={selPersonnel} onClose={closeDetail} />}
    </div>
  );
}
